/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import {
  Grid,
  Form,
  Button,
  Icon,
  Modal,
  Tooltip,
} from '@mairistem/composants';
import {
  PrenomsInput,
  SectionHeader,
  _string,
} from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';

import { ChampAdresse, ChampProfession } from '@/components/Common';
import { buildIndividuPutPayload, parseIndividuFiche } from '@/utils';
import { validationStepDeclarant } from '@/utils/validations';

interface ModalDeclarantProps {
  /** Endpoint générique des individus d'acte (`PUT {urlIndividus}/{id}`). */
  urlIndividus?: string;
  open: boolean;
  /** L'individu déclarant, tel que lu dans le dossier. */
  individu?: any;
  onClose: () => void;
  onSaved: (individu: any) => void;
}

/**
 * Périmètre : identité, âge, profession et domicile du déclarant.
 *
 * ⚠️ Pas de date de naissance ni de civilité : la base les laisse vides et porte
 * un ÂGE (`ai_age`). `EtatCivil` de la lib exigerait les deux.
 */
const CHAMPS_EDITABLES = [
  'nomFamille',
  'prenom',
  'prenomsComplementaires',
  'age',
  'profession',
  'adresseDomicile',
];

const ModalDeclarant = (props: ModalDeclarantProps) => {
  const { open, urlIndividus, individu, onClose, onSaved } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData(_.pick(parseIndividuFiche(individu), CHAMPS_EDITABLES));
    setAttempted(false);
  }, [open, individu]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleNomChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: string },
    ) => {
      setData((prev) => ({
        ...prev,
        [payload.name]: _string.normalizeNomFamille(payload.value),
      }));
    },
    [],
  );

  const handleAgeChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: string },
    ) => {
      setData((prev) => ({
        ...prev,
        [payload.name]: payload.value.replace(/[^0-9]/g, ''),
      }));
    },
    [],
  );

  /**
   * Même validateur que l'étape de l'assistant, appelé sous la forme qu'il
   * attend (`{ declarant }`) : une seule règle métier pour les deux écrans.
   */
  const errors = React.useMemo(
    () => validationStepDeclarant({ declarant: data })?.declarant ?? {},
    [data],
  );

  const displayedErrors = attempted ? errors : {};

  const handleSave = React.useCallback(() => {
    setAttempted(true);
    if (!_.isEmpty(errors)) {
      return;
    }

    if (!urlIndividus || _.isNil(individu?.identifiant)) {
      return;
    }

    const corps = buildIndividuPutPayload(individu, data);

    setSaving(true);
    request
      .put(`${urlIndividus}/${individu.identifiant}`, corps)
      .then(() => {
        onSaved({ ...individu, ...corps });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement du déclarant",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [errors, urlIndividus, individu, data, onSaved, onClose]);

  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header content="Modifier le déclarant" />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          {/* @ts-ignore */}
          <SectionHeader title="Identité" required />
          <Grid>
            <Grid.Row style={{ paddingLeft: '1em' }}>
              <Grid.Column computer={10} tablet={16} mobile={16}>
                <Tooltip
                  trigger={
                    <Form.Input
                      required
                      fluid
                      name="nomFamille"
                      label="Nom"
                      value={data?.nomFamille ?? ''}
                      error={!_.isNil(displayedErrors.nomFamille)}
                      onChange={handleNomChange}
                    />
                  }
                  type={displayedErrors.nomFamille ? 'error' : ''}
                  message={displayedErrors.nomFamille}
                />
              </Grid.Column>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <Tooltip
                  trigger={
                    <Form.Input
                      fluid
                      name="age"
                      label="Âge"
                      maxLength={3}
                      value={_.toString(data?.age ?? '')}
                      error={!_.isNil(displayedErrors.age)}
                      onChange={handleAgeChange}
                    />
                  }
                  type={displayedErrors.age ? 'error' : ''}
                  message={displayedErrors.age}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row style={{ paddingLeft: '1em' }}>
              <Grid.Column width={16}>
                <PrenomsInput
                  prenom={data?.prenom ?? ''}
                  prenomsComplementaires={data?.prenomsComplementaires ?? ''}
                  errors={displayedErrors}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row style={{ paddingLeft: '1em' }}>
              <Grid.Column computer={8} tablet={16} mobile={16}>
                <ChampProfession
                  name="profession"
                  value={data?.profession}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <ChampAdresse
            data={data}
            onChange={handleChange}
            name="adresseDomicile"
            title="Adresse de domicile"
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          data-testid="declarant-enregistrer"
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalDeclarant;
